import { Input, Row, Col, message, Checkbox } from 'antd';
import { Button } from '../../../../../../button';
import { ClearOutlined, SaveOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import { Zone } from '../../../../../../../@types/projects/types';
import { useSelected } from '../../../../../../../providers/selectedContext';
import { useAppContext } from '../../../../../../../providers/AppContext';
import { deleteZone, updateZoneBoundaries } from '../../../../../../../api/data';
import { ConfirmDeleteAllModal } from './ConfirmDeleteAllModal';

type FormValues = {
  zones: Zone[];
};

const CreatedZones = () => {
  const [loadingAll, setLoadingAll] = useState(false);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [initialZones, setInitialZones] = useState<Zone[]>([]);
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { upsertProject } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { control, reset, getValues } = useForm<FormValues>({
    defaultValues: { zones: [] }
  });

  const { fields } = useFieldArray({
    control,
    name: 'zones',
    keyName: 'customId'
  });

  const watchedZones = useWatch({ control, name: 'zones' });

  useEffect(() => {
    if (selectedInfo.project?.correlation?.zones) {
      reset({ zones: selectedInfo.project.correlation.zones });
      setInitialZones(selectedInfo.project.correlation.zones);
      setSelectedIds([]);
    }
  }, [selectedInfo.project, reset]);

  const isDirty = (index: number): boolean => {
    const current = watchedZones?.[index];
    const initial = initialZones[index];
    if (!current || !initial) return false;
    return (
      String(current.start) !== String(initial.start) ||
      String(current.end) !== String(initial.end) ||
      String(current.mean) !== String(initial.mean) ||
      current.color !== initial.color
    );
  };

  const handleSave = async (index: number) => {
    if (!selectedInfo.project) return;
    const zone = getValues(`zones.${index}`);
    const start = parseFloat(String(zone.start).replace(',', '.'));
    const end = parseFloat(String(zone.end).replace(',', '.'));
    const mean = parseFloat(String(zone.mean).replace(',', '.'));

    setSavingIndex(index);
    try {
      const response = await updateZoneBoundaries(selectedInfo.project.uid, {
        project_uid: selectedInfo.project.uid,
        zones: [{
          zone_uid: zone.uid,
          start: isNaN(start) ? undefined : start,
          end: isNaN(end) ? undefined : end,
          mean: isNaN(mean) ? undefined : mean,
          color: zone.color ?? undefined,
        }]
      });
      setSelectedInfo({ project: response });
      upsertProject(response);
      message.success('Rock Type updated successfully');
    } catch (error) {
      message.error('Error updating zone');
      console.error(error);
    } finally {
      setSavingIndex(null);
    }
  };

  const handleConfirmDeleteAll = async () => {
    if (!selectedInfo.project || selectedIds.length === 0) {
      message.info('Não há zonas selecionadas para deletar.');
      return;
    }

    const totalZones = selectedIds.length;
    const projectUid = selectedInfo.project.uid;
    const selectedZoneUids = selectedIds;

    setLoadingAll(true);
    setIsModalOpen(false);
    try {
      const response = await deleteZone(projectUid, selectedZoneUids);
      setSelectedInfo({ project: response });
      upsertProject(response);
      setSelectedIds([]);
      message.success(`${totalZones} selected rock types were deleted successfully!`);
    } catch (error) {
      message.error('Error deleting selected zones');
      console.error(error);
    } finally {
      setLoadingAll(false);
    }
  };

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
        <Col>
          <p style={{ margin: 0, fontWeight: 'bold' }}>Flow units created ({fields.length})</p>
        </Col>
        <Col>
          <Button
            variant="secondary"
            startIcon={<ClearOutlined />}
            loading={loadingAll}
            disabled={selectedIds.length === 0}
            onClick={() => {
              if (!selectedInfo.project || selectedIds.length === 0) {
                message.info('Não há zonas selecionadas para deletar.');
                return;
              }
              setIsModalOpen(true);
            }}
          >
            Remove Selected
          </Button>
        </Col>
      </Row>

      {fields.length > 0 ? (
        fields.map((field, index) => (
          <Row
            gutter={6}
            style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
            key={field.customId}
          >
            <Col span={2} style={{ display: 'flex', justifyContent: 'center' }}>
              <Checkbox
                checked={selectedIds.includes(field.uid)}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setSelectedIds(prev =>
                    checked ? [...prev, field.uid] : prev.filter(id => id !== field.uid)
                  );
                }}
              />
            </Col>
            <Col span={3} style={{ whiteSpace: 'nowrap' }}>
              <small>Rock type {index + 1}</small>
            </Col>
            <Col span={5}>
              <Controller
                name={`zones.${index}.start`}
                control={control}
                render={({ field: controllerField }) => (
                  <Input
                    {...controllerField}
                    value={String(controllerField.value ?? '')}
                    placeholder="Min"
                    type="text"
                    inputMode="decimal"
                    size="small"
                  />
                )}
              />
            </Col>
            <Col span={5}>
              <Controller
                name={`zones.${index}.mean`}
                control={control}
                render={({ field: controllerField }) => (
                  <Input
                    {...controllerField}
                    value={String(controllerField.value ?? '')}
                    placeholder="Mean"
                    type="text"
                    inputMode="decimal"
                    size="small"
                  />
                )}
              />
            </Col>
            <Col span={5}>
              <Controller
                name={`zones.${index}.end`}
                control={control}
                render={({ field: controllerField }) => (
                  <Input
                    {...controllerField}
                    value={String(controllerField.value ?? '')}
                    placeholder="Max"
                    type="text"
                    inputMode="decimal"
                    size="small"
                  />
                )}
              />
            </Col>
            <Col span={4} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Controller
                name={`zones.${index}.color`}
                control={control}
                render={({ field: controllerField }) => (
                  <input
                    type="color"
                    {...controllerField}
                    value={controllerField.value ?? '#1677ff'}
                    style={{ width: '32px', height: '24px', padding: '1px', border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
                  />
                )}
              />
              {isDirty(index) && (
                <Button
                  variant="primary"
                  size="sm"
                  startIcon={<SaveOutlined />}
                  loading={savingIndex === index}
                  onClick={() => handleSave(index)}
                  style={{ flexShrink: 0 }}
                />
              )}
            </Col>
          </Row>
        ))
      ) : (
        <p style={{ textAlign: 'center', color: '#999' }}>No zones created yet.</p>
      )}

      <ConfirmDeleteAllModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmDeleteAll}
        zoneCount={fields.length}
      />
    </>
  );
};

export default CreatedZones;
